/**
 * Academy figure icons.
 *
 * Several AMC questions are built ON artwork: a spinner whose sectors carry
 * pictures, a map grid whose cells carry symbols, a logic grid of pictographs.
 * `image_key` cannot serve these -- it replaces the WHOLE figure with one
 * raster, whereas these marks have to sit INSIDE a vector figure at a measured
 * position, and the surrounding geometry (sector dividers, grid lines) is what
 * the transcriber measured off the page.
 *
 * The assets are our own, drawn from scratch against briefs in
 * research/academy-question-banks/CODEX-ICON-BRIEFS.md -- deliberately NOT
 * traced from the Australian Maths Trust originals, so unlike an `image_key`
 * crop they are product content we may ship.
 *
 * They are imported as raw text and inlined into the figure's own <svg> rather
 * than referenced with <img>/<image>, for two reasons: every mark is stroked in
 * `currentColor`, so inlining makes it inherit the figure's ink (including the
 * dark-theme flip), and it keeps the figure network-free, which is what lets
 * the offline render harness rasterise it.
 */
import glyphMark from '@/assets/academy/icons/glyph-mark.svg?raw';
import hand from '@/assets/academy/icons/hand.svg?raw';
import house from '@/assets/academy/icons/house.svg?raw';
import jigsawScene from '@/assets/academy/icons/jigsaw-scene.svg?raw';
import letterA from '@/assets/academy/icons/letter-a.svg?raw';
import letterC from '@/assets/academy/icons/letter-c.svg?raw';
import letterH from '@/assets/academy/icons/letter-h.svg?raw';
import letterM from '@/assets/academy/icons/letter-m.svg?raw';
import letterS from '@/assets/academy/icons/letter-s.svg?raw';
import letterT from '@/assets/academy/icons/letter-t.svg?raw';
import magpie from '@/assets/academy/icons/magpie.svg?raw';
import person from '@/assets/academy/icons/person.svg?raw';
import rabbit from '@/assets/academy/icons/rabbit.svg?raw';
import snake from '@/assets/academy/icons/snake.svg?raw';
import sun from '@/assets/academy/icons/sun.svg?raw';
import topHat from '@/assets/academy/icons/top-hat.svg?raw';
import toyCar from '@/assets/academy/icons/toy-car.svg?raw';
import tree from '@/assets/academy/icons/tree.svg?raw';

export type AcademyIcon = { viewBox: [number, number, number, number]; inner: string };

/** Pull the viewBox and the drawable body out of a standalone SVG file. */
function parse(raw: string): AcademyIcon {
  const vb = /viewBox="([-\d.\s]+)"/.exec(raw);
  const body = /<svg[^>]*>([\s\S]*)<\/svg>/.exec(raw);
  if (!vb || !body) throw new Error('academy icon is not a parseable standalone SVG');
  const nums = vb[1].trim().split(/\s+/).map(Number);
  if (nums.length !== 4 || nums.some((n) => !Number.isFinite(n))) {
    throw new Error(`academy icon has an unusable viewBox: ${vb[1]}`);
  }
  return { viewBox: nums as [number, number, number, number], inner: body[1].trim() };
}

const RAW: Record<string, string> = {
  'glyph-mark': glyphMark,
  hand,
  house,
  'jigsaw-scene': jigsawScene,
  'letter-a': letterA,
  'letter-c': letterC,
  'letter-h': letterH,
  'letter-m': letterM,
  'letter-s': letterS,
  'letter-t': letterT,
  magpie,
  person,
  rabbit,
  snake,
  sun,
  'top-hat': topHat,
  'toy-car': toyCar,
  tree,
};

export const ACADEMY_ICONS: Record<string, AcademyIcon> = Object.fromEntries(
  Object.entries(RAW).map(([k, v]) => [k, parse(v)]),
);

export const ACADEMY_ICON_KEYS = Object.keys(ACADEMY_ICONS).sort();
